import { useMutation, UseMutationOptions } from '@tanstack/react-query';

import { ResourceType, useResourceCallMutation } from '../../resources';
import { TicketStatus } from '../core/types';
import { validateReason, validateTicketIds } from '../utils/ticket-hooks.utils';

export type TicketTransition =
	| 'open'
	| 'start'
	| 'undoStart'
	| 'close'
	| 'undoClose'
	| 'cancel'
	| 'undoCancel'
	| 'archive'
	| 'undoArchive'
	| 'undoOpen';

interface SetTicketsTransitionResponse {
	_id: string[];
}

export interface SetTicketTransitionVariables {
	ticketId: string;
	transition: TicketTransition;
	reason?: string;
}

interface SetTicketsTransitionVariables {
	ticketIds: string[];
	transition: TicketTransition;
	reason?: string;
}

const getMethodForTransition = (transition: TicketTransition): string => {
	return transition;
};

export const useSetTicketsTransitionMutation = <TError = unknown, TContext = unknown>(
	options?: UseMutationOptions<SetTicketsTransitionResponse, TError, SetTicketsTransitionVariables, TContext>
) => {
	const { mutateAsync: callSetTransition } = useResourceCallMutation<SetTicketsTransitionResponse, TError, TContext>({
		resourceType: ResourceType.TICKETS,
		responseFields: ['id'],
	});

	return useMutation<SetTicketsTransitionResponse, TError, SetTicketsTransitionVariables, TContext>(
		async ({ ticketIds, transition, reason }) => {
			validateTicketIds(ticketIds);
			const params = reason ? { message: validateReason(reason) } : {};
			const ticketId = ticketIds.join(',');

			return await callSetTransition({
				resourceId: ticketId,
				method: getMethodForTransition(transition),
				params,
				httpMethod: 'GET',
			});
		},
		options
	);
};

interface PossibleTransitionsOptions {
	status: string;
	isArchived: boolean;
}
export const getPossibleTransitions = (ticket: PossibleTransitionsOptions) => {
	const possibleTransitions: TicketTransition[] = [];
	if (ticket.status === TicketStatus.DRAFT) {
		possibleTransitions.push('open');
		possibleTransitions.push('cancel');
	} else if (ticket.status === TicketStatus.OPEN) {
		possibleTransitions.push('start');
		possibleTransitions.push('cancel');
		// possibleTransitions.push('undoOpen');
	} else if (ticket.status === TicketStatus.IN_PROGRESS) {
		possibleTransitions.push('close');
		possibleTransitions.push('undoStart');
		possibleTransitions.push('cancel');
	} else if (ticket.status === TicketStatus.CLOSED) {
		if (ticket.isArchived) {
			possibleTransitions.push('undoArchive');
		} else {
			possibleTransitions.push('archive');
			possibleTransitions.push('undoClose');
		}
	} else if (ticket.status === TicketStatus.CANCELLED) {
		if (ticket.isArchived) {
			possibleTransitions.push('undoArchive');
		} else {
			possibleTransitions.push('undoCancel');
			possibleTransitions.push('archive');
		}
	}
	return possibleTransitions;
};
